import { IconX } from "@tabler/icons-react";
import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { errorText } from "./api";
import { Drawer } from "./drawer";
import { useNarrow } from "./responsive";

export type Tone = "ok" | "warn" | "bad" | "info" | "brand" | "neutral";

export function Badge({ tone = "neutral", children, title }: { tone?: Tone; children: ReactNode; title?: string }) {
  return <span className={`badge ${tone === "neutral" ? "" : tone}`} title={title}>{children}</span>;
}

export type Notice = { tone: "ok" | "warn" | "bad"; text: string } | undefined;

export function Banner({ notice, onClose }: { notice: Notice; onClose?: () => void }) {
  if (!notice) return null;
  return (
    <div className={`banner ${notice.tone}`} role={notice.tone === "bad" ? "alert" : "status"}>
      <div className="row" style={{ alignItems: "flex-start", flexWrap: "nowrap" }}>
        <div style={{ flex: 1 }}>{notice.text}</div>
        {onClose && <button className="ghost icon" style={{ height: 22, width: 22 }} onClick={onClose} aria-label="Dismiss notice"><IconX size={14} /></button>}
      </div>
    </div>
  );
}

export function PageHead({ title, sub, children }: { title: string; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div>
        <h1>{title}</h1>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {children && <div className="row">{children}</div>}
    </div>
  );
}

export function Metric({ label, value, tone, hint, delta }: { label: string; value: ReactNode; tone?: "warn"; hint?: string; delta?: ReactNode }) {
  return (
    <div className={`metric ${tone ?? ""}`}>
      <div className="k">{label}</div>
      <div className="v">{value}</div>
      {delta && <div className="delta">{delta}</div>}
      {hint && <div className="help">{hint}</div>}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="empty">{children}</div>;
}

const FieldLabel = createContext<string | undefined>(undefined);

/**
 * The id of the enclosing Field's label text. Controls that put extra elements inside the label (Base UI
 * adds a hidden "Dismiss" button next to an open input) name themselves with it via aria-labelledby.
 */
export const useFieldLabelId = () => useContext(FieldLabel);

export function Field({ label, help, error, children }: { label: string; help?: ReactNode; error?: string | undefined; children: ReactNode }) {
  const id = useId();
  const noteId = `${id}-note`;
  const ref = useRef<HTMLLabelElement>(null);
  const hasNote = Boolean(error || help);
  // Ties the help or error text to the control (aria-describedby, aria-invalid), so a screen reader reads it
  // with the field; the control is the input, select, textarea or kit control (Select, Checkbox…) the Field wraps.
  useLayoutEffect(() => {
    const control = ref.current?.querySelector<HTMLElement>('input:not([type="hidden"]):not([aria-hidden="true"]), select, textarea, [role="combobox"], [role="checkbox"], [role="radiogroup"]');
    if (!control || !hasNote) return;
    const tokens = (el: HTMLElement) => (el.getAttribute("aria-describedby") ?? "").split(/\s+/).filter((t) => t && t !== noteId);
    control.setAttribute("aria-describedby", [...tokens(control), noteId].join(" "));
    const invalid = Boolean(error) && !control.hasAttribute("aria-invalid");
    if (invalid) control.setAttribute("aria-invalid", "true");
    return () => {
      const rest = tokens(control);
      if (rest.length) control.setAttribute("aria-describedby", rest.join(" "));
      else control.removeAttribute("aria-describedby");
      if (invalid) control.removeAttribute("aria-invalid");
    };
  });
  return (
    <label className="field" ref={ref}>
      <span id={id}>{label}</span>
      <FieldLabel.Provider value={id}>{children}</FieldLabel.Provider>
      {error ? <span id={noteId} className="error-text">{error}</span> : help ? <span id={noteId} className="help">{help}</span> : null}
    </label>
  );
}

export function Panel({ title, sub, onClose, children, actions }: { title: ReactNode; sub?: ReactNode; onClose?: () => void; children: ReactNode; actions?: ReactNode }) {
  const narrow = useNarrow();
  // On a phone the panel would land below the whole list: it opens as a sheet from the bottom instead.
  if (narrow && onClose) {
    return (
      <Drawer open onOpenChange={(open) => !open && onClose()} title={title} side="bottom" className="panel-sheet">
        <div className="panel-sheet-body">
          {sub && <div className="muted small">{sub}</div>}
          {children}
          {actions && <div className="actions">{actions}</div>}
        </div>
      </Drawer>
    );
  }
  return (
    <aside className="panel" aria-label={typeof title === "string" ? title : undefined}>
      <div className="panel-head">
        <div>
          <h2>{title}</h2>
          {sub && <div className="muted small" style={{ marginTop: 2 }}>{sub}</div>}
        </div>
        {onClose && <button className="ghost icon" onClick={onClose} aria-label="Close panel"><IconX size={18} /></button>}
      </div>
      {children}
      {actions && <div className="actions">{actions}</div>}
    </aside>
  );
}

/** Loads data asynchronously; `reload` fetches again. Responses to stale requests are ignored. */
export function useAsync<T>(load: () => Promise<T>, deps: unknown[]): { data: T | undefined; error: string | undefined; loading: boolean; reload: () => void } {
  const [state, setState] = useState<{ data: T | undefined; error: string | undefined; loading: boolean }>({ data: undefined, error: undefined, loading: true });
  const seq = useRef(0);
  const run = useCallback(() => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: undefined }));
    load().then(
      (data) => { if (mine === seq.current) setState({ data, error: undefined, loading: false }); },
      (err) => { if (mine === seq.current) setState((s) => ({ ...s, error: errorText(err), loading: false })); },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  useEffect(run, [run]);
  return { ...state, reload: run };
}

/** Stores a per-user preference in this browser; without storage it stays in memory only. */
export function useStoredState<T extends string>(key: string, initial: T): [T, (v: T) => void] {
  const [value, setValue] = useState<T>(() => {
    try {
      return (localStorage.getItem(key) as T | null) ?? initial;
    } catch {
      return initial;
    }
  });
  const set = (v: T) => {
    setValue(v);
    try {
      localStorage.setItem(key, v);
    } catch {
      /* no storage */
    }
  };
  return [value, set];
}
